import { CrmRow } from './compareRow';

export type ParseError =
  | { code: 'missing_header_row' }
  | { code: 'invalid_header'; expected: string[]; actual: string[] }
  | { code: 'empty_file' };

export type ParseResult =
  | { ok: true; rows: CrmRow[] }
  | { ok: false; error: ParseError };

// Required template columns A–L. These are the customer fields the UAT documents in section 2.5.
const EXPECTED_HEADER = [
  'Account Number',
  'Customer Name',
  'ABN',
  'ACN',
  'Email Address',
  'Mobile Number',
  'Street',
  'Town',
  'Region',
  'Postcode',
  'Country',
  'Is Active',
] as const;

// Optional 13th column — populated by the static template shipped with this feature, used to
// pre-fill the per-row Note (Enhanced Notes, CR-SEQ-2026-013). The column is only read when its
// header is exactly "Note"; any other column-M header is rejected so a renamed column can't be
// silently treated as the per-row note.
const OPTIONAL_NOTE_HEADER = 'Note';
const NOTE_COLUMN_INDEX = EXPECTED_HEADER.length; // 12 → column M

export function parseUsersSheet(sheet: unknown[][]): ParseResult {
  const headerIndex = sheet.findIndex(row =>
    Array.isArray(row) && String(row[0] ?? '').trim() === 'Account Number',
  );
  if (headerIndex === -1) return { ok: false, error: { code: 'missing_header_row' } };

  const headerCells = sheet[headerIndex].map(c => String(c ?? '').trim());
  const requiredHeadersMatch =
    headerCells.length >= EXPECTED_HEADER.length &&
    EXPECTED_HEADER.every((expected, i) => headerCells[i] === expected);
  // Column M, if present, must be exactly "Note". Reject anything else so an unrelated extra
  // column does not get silently captured as the per-row note.
  const extraColumn = headerCells[NOTE_COLUMN_INDEX];
  const noteHeaderValid = extraColumn === undefined || extraColumn === '' || extraColumn === OPTIONAL_NOTE_HEADER;
  if (!requiredHeadersMatch || !noteHeaderValid) {
    return {
      ok: false,
      error: { code: 'invalid_header', expected: [...EXPECTED_HEADER, OPTIONAL_NOTE_HEADER], actual: headerCells },
    };
  }
  const hasNoteColumn = extraColumn === OPTIONAL_NOTE_HEADER;

  const rows: CrmRow[] = [];
  sheet.slice(headerIndex + 1).forEach((raw, i) => {
    const cells = (Array.isArray(raw) ? raw : []).map(c => String(c ?? '').trim());
    if (cells.every(c => c === '')) return;
    rows.push({
      rowIndex: headerIndex + 2 + i,
      accountNumber: cells[0] ?? '',
      customerName: cells[1] ?? '',
      abn: cells[2] ?? '',
      acn: cells[3] ?? '',
      email: cells[4] ?? '',
      mobile: cells[5] ?? '',
      street: cells[6] ?? '',
      town: cells[7] ?? '',
      region: cells[8] ?? '',
      postcode: cells[9] ?? '',
      country: cells[10] ?? '',
      isActive: cells[11] ?? '',
      note: hasNoteColumn ? cells[NOTE_COLUMN_INDEX] ?? '' : '',
    });
  });

  return { ok: true, rows };
}

export { EXPECTED_HEADER, OPTIONAL_NOTE_HEADER };
